import React, { useState, useEffect } from 'react';
import { getPhoneId } from '../utils/phoneId';
import './UserRequests.css';
import { API_URL } from '../config/api';

const UserRequests = () => {
  const [phoneId, setPhoneId] = useState(null);
  const [user, setUser] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const id = getPhoneId();
    setPhoneId(id);
    
    if (id) {
      identifyUser(id).then(() => {
        fetchUserRequests(id);
      });
    }
  }, []);

  const identifyUser = async (phoneId) => {
    try {
      const response = await fetch(`${API_URL}/api/users/identify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phone_id: phoneId }),
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        return userData;
      }
    } catch (error) {
      console.error('Erreur lors de l\'identification:', error);
    }
    return null;
  };

  const fetchUserRequests = async (phoneId) => {
    try {
      setLoading(true);
      // D'abord obtenir l'ID de l'utilisateur
      const userResponse = await fetch(`${API_URL}/api/users/identify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phone_id: phoneId }),
      });

      if (userResponse.ok) {
        const userData = await userResponse.json();
        const userId = userData.id;

        // Ensuite obtenir les demandes de cet utilisateur
        const requestsResponse = await fetch(`${API_URL}/api/requests/user/${userId}`);
        if (requestsResponse.ok) {
          const requestsData = await requestsResponse.json();
          setRequests(requestsData);
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement des demandes:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { class: 'status-pending', text: '⏳ En attente', icon: '⏳' },
      approved: { class: 'status-approved', text: '✅ Approuvée', icon: '✅' },
      rejected: { class: 'status-rejected', text: '❌ Refusée', icon: '❌' }
    };
    const badge = badges[status] || { class: '', text: status, icon: '' };
    return <span className={`status-badge ${badge.class}`}>{badge.text}</span>;
  };

  if (loading) {
    return (
      <div className="user-requests">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Chargement de vos demandes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="user-requests">
      <div className="user-requests-header">
        <h1>📋 Mes demandes</h1>
        {user && (
          <p className="user-name">Bonjour, {user.name}</p>
        )}
      </div>

      {requests.length === 0 ? (
        <div className="no-requests">
          <div className="no-requests-icon">📭</div>
          <h2>Aucune demande</h2>
          <p>Vous n'avez pas encore fait de demande.</p>
          <p>Scannez le QR code pour créer une demande.</p>
        </div>
      ) : (
        <div className="requests-list">
          {requests.map((request) => (
            <div key={request.id} className={`request-card ${request.status}`}>
              <div className="request-header">
                <div className="request-status">
                  {getStatusBadge(request.status)}
                </div>
                <div className="request-date">
                  {formatDate(request.created_at)}
                </div>
              </div>

              <div className="request-content">
                {request.status === 'pending' && (
                  <div className="request-info pending-info">
                    <p className="info-text">
                      ⏳ Votre demande est en attente de traitement par l'administrateur.
                    </p>
                    <p className="info-text">
                      L'administrateur va choisir l'élément à vous attribuer.
                    </p>
                  </div>
                )}

                {request.status === 'approved' && (
                  <div className="request-info approved-info">
                    <div className="success-message">
                      <p className="success-icon">✅</p>
                      <div>
                        <p className="success-title">Demande approuvée !</p>
                        {request.approved_item_name && (
                          <p className="item-name">
                            Élément attribué : <strong>{request.approved_item_name}</strong>
                            {request.approved_item_size && (
                              <span className="item-size"> ({request.approved_item_size})</span>
                            )}
                          </p>
                        )}
                        {request.processed_at && (
                          <p className="processed-date">
                            Traité le : {formatDate(request.processed_at)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {request.status === 'rejected' && (
                  <div className="request-info rejected-info">
                    <p className="error-text">
                      ❌ Votre demande a été refusée.
                    </p>
                    {request.rejection_reason && (
                      <p className="reason-text">
                        Raison : {request.rejection_reason}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="user-actions">
        <button 
          className="btn-primary"
          onClick={() => {
            // Retourner à l'onglet scan dans UserApp
            const event = new CustomEvent('switchToScan');
            window.dispatchEvent(event);
          }}
        >
          📷 Scanner un QR Code
        </button>
        <button 
          className="btn-secondary"
          onClick={() => fetchUserRequests(phoneId)}
        >
          🔄 Actualiser
        </button>
      </div>
    </div>
  );
};

export default UserRequests;
